/**
 * OutletSelector Component
 *
 * Displays a list of active outlets for authentication.
 * Fetches outlets from API and handles loading/error states.
 * This implementation is API-only and does not use IndexedDB caching.
 */

import { useEffect, useState } from 'react';
import type { OutletPublicInfo } from '@photobooth/types';

interface SelectedOutletInfo {
  outletId: string;
  name: string;
  location?: string;
}

interface OutletSelectorProps {
  onSelect: (outlet: SelectedOutletInfo) => void;
  apiClient: {
    outlets: {
      getActiveOutlets: () => Promise<{
        data?: OutletPublicInfo[];
        error?: any;
        success: boolean;
      }>;
    };
  };
}

export function OutletSelector({ onSelect, apiClient }: OutletSelectorProps) {
  const [outlets, setOutlets] = useState<OutletPublicInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await apiClient.outlets.getActiveOutlets();

        if (!response.success || !response.data) {
          throw new Error(response.error?.message || 'Failed to fetch outlets');
        }

        setOutlets(response.data);
      } catch (err) {
        console.error('Error fetching outlets:', err);
        setError(err instanceof Error ? err.message : 'Failed to load outlets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOutlets();
  }, [apiClient]);

  const handleRetry = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await apiClient.outlets.getActiveOutlets();

      if (response.success && response.data) {
        setOutlets(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch outlets');
      }
    } catch (err) {
      console.error('Error fetching outlets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load outlets');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center py-12">
          <div className="text-6xl mb-4 animate-bounce">📍</div>
          <div className="text-xl font-semibold text-gray-700">Loading outlets...</div>
          <div className="text-sm text-gray-500 mt-2">Please wait</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h3 className="text-xl font-bold text-red-800 mb-2">Error Loading Outlets</h3>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (outlets.length === 0) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-8 text-center">
          <div className="text-5xl mb-4">🏪</div>
          <h3 className="text-xl font-bold text-yellow-800 mb-2">No Active Outlets</h3>
          <p className="text-yellow-600">There are no active outlets available at this time.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">
        Select Outlet
      </h2>
      <p className="text-center text-gray-600 mb-8">
        Choose the outlet location for this device
      </p>

      <div className="grid grid-cols-1 gap-4">
        {outlets.map((outlet) => (
          <button
            key={outlet.outletId}
            onClick={() =>
              onSelect({
                outletId: outlet.outletId,
                name: outlet.name,
                location: outlet.location,
              })
            }
            className="group relative overflow-hidden rounded-2xl bg-white border-2 border-gray-200 p-6 text-left shadow-md hover:shadow-xl hover:border-purple-400 transition-all duration-300 hover:scale-[1.02]"
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center text-3xl">
                🏪
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-purple-600 transition-colors">
                  {outlet.name}
                </h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <span className="text-sm">📍</span>
                  <span className="text-sm">{outlet.location}</span>
                </div>
                {outlet.address && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                    {outlet.address}
                  </p>
                )}
                
                <div className="mt-3 flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    Active
                  </span>
                </div>
              </div>
              
              <div className="flex-shrink-0 text-gray-400 group-hover:text-purple-500 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition-all" />
          </button>
        ))}
      </div>
    </div>
  );
}
